import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { authActions } from "../../../context/actions/authActions";
import { ERROR_TYPES } from "../../../context/reducers/errorReducers";
import useStore from "../../../context/useStore";
import PageHorsConnexion from "../../common/HorsConnexion/PageHorsConnexion";
import CodeVerification, { LONGUEUR_CODE } from "./CodeVerification";
import ForgotPassword from "./ForgotPassword";
import FormulaireConnexion from "./FormulaireConnexion";

/**
 * Page de connexion du back-office (maquette 05) : identifiants, puis, hors
 * réseau CAF, code de vérification reçu par e-mail ; mot de passe oublié en
 * vue séparée. La logique (appels, erreurs, minuteries) est celle de la
 * version précédente ; seul l'habillage change.
 */
const Login = () => {
  const [state, dispatch] = useStore();
  const actions = authActions(state, dispatch);
  // Les pages « lien reçu par e-mail » arrivent ici avec { vue: "oubli", email } :
  // on ouvre la bonne vue, on préremplit l'adresse, puis on oublie cet état
  // pour qu'un rechargement revienne à la connexion.
  const location = useLocation();
  const history = useHistory();
  const arrivee = (location && location.state) || {};
  const [login, setLogin] = useState(arrivee.email || "");
  const [password, setPassword] = useState("");
  const [forgotPassword, setForgotPassword] = useState(arrivee.vue === "oubli");
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canLoggin, setCanLoggin] = useState(true);
  // Étape « code de vérification » MFA envoyé par e-mail
  const [mfa, setMfa] = useState(null); // { challenge, emailHint, expiresIn }
  const [code, setCode] = useState("");
  const [secondesAvantRenvoi, setSecondesAvantRenvoi] = useState(0);
  const [secondesValidite, setSecondesValidite] = useState(0);

  useEffect(() => {
    if (location && location.state && history) {
      history.replace({ ...location, state: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Quitte l'étape du code MFA et repart de zéro : identifiants + erreurs vidés. */
  const quitterEtapeMfa = useCallback(() => {
    setMfa(null);
    setCode("");
    dispatch({ type: ERROR_TYPES.CLEAR_ERRORS });
  }, [dispatch]);

  useEffect(() => {
    if (Object.values(state.errors).length > 0) {
      setIsLoading(false);
      if (
        !mfa &&
        (state.errors.password || state.errors.nbAttempts || state.errors.timer || state.errors.other)
      ) {
        // Identifiants refusés ou compte bloqué : le mot de passe est vidé pour une nouvelle saisie.
        setPassword("");
      }
      if (state.errors.timer) {
        setTimer(parseInt(state.errors.timer));
      }
      if (state.errors.restart) {
        // Défi MFA épuisé, invalide ou inconnu : on repart de l'étape identifiants.
        quitterEtapeMfa();
      }
      if (state.errors.code) {
        // Code faux ou expiré : on vide les cases pour ressaisie.
        setCode("");
      }
      if (state.errors.retryIn) {
        setSecondesAvantRenvoi(parseInt(state.errors.retryIn));
      }
    }
  }, [state.errors, quitterEtapeMfa, mfa]);

  useEffect(() => {
    if (!mfa) return undefined;
    const interval = setInterval(() => {
      setSecondesAvantRenvoi((s) => (s > 0 ? s - 1 : 0));
      setSecondesValidite((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [mfa]);

  useEffect(() => {
    setTimer(0);
    setCanLoggin(true);
  }, [forgotPassword]);

  useEffect(() => {
    if (timer > 0) {
      setCanLoggin(false);
    }
  }, [timer]);

  function loginHandle(e) {
    e.preventDefault();
    setIsLoading(true);
    actions.loginUser({ login, password }, demarrerMfa);
  }

  function demarrerMfa(defi) {
    setIsLoading(false);
    setMfa(defi);
    setCode("");
    setSecondesAvantRenvoi(30);
    setSecondesValidite(defi.expiresIn || 300);
  }

  function validerCode() {
    if (isLoading || code.length !== LONGUEUR_CODE || secondesValidite <= 0) return;
    setIsLoading(true);
    actions
      .verifyMfaCode({ challenge: mfa.challenge, code })
      .finally(() => setIsLoading(false));
  }

  function validerCodeHandle(e) {
    e.preventDefault();
    validerCode();
  }

  // Le sixième chiffre saisi (ou le code collé) lance la vérification, sans
  // clic sur « Valider le code ». Un code refusé vide les cases : la ressaisie
  // relance la vérification ; une panne réseau garde le code et laisse le
  // bouton pour réessayer.
  useEffect(() => {
    if (mfa && code.length === LONGUEUR_CODE) validerCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  function timerFinish() {
    setTimer(0);
    setCanLoggin(true);
  }

  return (
    <PageHorsConnexion>
      {forgotPassword ? (
        <ForgotPassword emailInitial={login} retour={() => setForgotPassword(false)} />
      ) : mfa ? (
        <CodeVerification
          defi={mfa}
          code={code}
          onCode={setCode}
          onSubmit={validerCodeHandle}
          erreurs={state.errors}
          isLoading={isLoading}
          secondesValidite={secondesValidite}
          secondesAvantRenvoi={secondesAvantRenvoi}
          onRenvoyer={() => actions.resendMfaCode({ challenge: mfa.challenge }, demarrerMfa)}
          onRecommencer={quitterEtapeMfa}
        />
      ) : (
        <FormulaireConnexion
          login={login}
          password={password}
          onLogin={setLogin}
          onPassword={setPassword}
          onSubmit={loginHandle}
          erreurs={state.errors}
          isLoading={isLoading}
          bloque={!canLoggin}
          timer={timer}
          onFinBlocage={timerFinish}
          onMotDePasseOublie={() => setForgotPassword(true)}
        />
      )}
    </PageHorsConnexion>
  );
};

export default Login;
